import { Box } from "@rebass/grid";
import logger from "@rentiohq/shared-frontend/dist/logger";
import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import { useBrokerFeature } from "@rentiohq/shared-frontend/dist/redux/broker/broker.hooks";
import * as contactActions from "@rentiohq/shared-frontend/dist/redux/contact/contact.actions";
import * as contactApi from "@rentiohq/shared-frontend/dist/redux/contact/contact.api";
import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import { generateFormId } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import {
  ISavedApplicationFilter,
  IFilterCriteria,
} from "@rentiohq/shared-frontend/dist/reduxV2/applicationFilters/types";
import * as applicationActions from "@rentiohq/shared-frontend/dist/reduxV2/applications/application.actions";
import * as applicationHooks from "@rentiohq/shared-frontend/dist/reduxV2/applications/application.hooks";
import * as propertyActions from "@rentiohq/shared-frontend/dist/reduxV2/property/property.actions";
import * as propertyApi from "@rentiohq/shared-frontend/dist/reduxV2/property/property.api";
import * as propertyAmenityHooks from "@rentiohq/shared-frontend/dist/reduxV2/propertyAmenity/propertyAmenity.hooks";
import { getAmenityNumberValue } from "@rentiohq/shared-frontend/dist/reduxV2/propertyAmenity/propertyAmenity.utils";
import {
  EIntakeStep,
  IApplication,
  IApplicationContact,
} from "@rentiohq/shared-frontend/dist/types/application.types";
import { EBrokerFeature } from "@rentiohq/shared-frontend/dist/types/broker.types";
import {
  EContactInitialType,
  IContact,
} from "@rentiohq/shared-frontend/dist/types/contact.types";
import {
  EPropertyMemberTypes,
  IProperty,
} from "@rentiohq/shared-frontend/dist/types/property.types";
import { IPropertyAmenity } from "@rentiohq/shared-frontend/dist/types/propertyAmenity.types";
import api from "@rentiohq/shared-frontend/dist/utils/api/api.utils";
import { confirm } from "@rentiohq/shared-frontend/dist/utils/confirm.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { getTenantProfileUrl } from "@rentiohq/shared-frontend/dist/utils/property.utils";
import { calculateRentMatchScore } from "@rentiohq/shared-frontend/dist/utils/rentmatch.utils";
import {
  Button,
  Card,
  MultiStepForm,
  DropdownMenu,
  Switch,
  Modal,
  EmptyState,
  ESpacings,
  Tooltip,
} from "@rentiohq/web-shared/dist/components";
import { toast } from "@rentiohq/web-shared/dist/managers/Toast";
import * as systemSelectors from "@rentiohq/web-shared/dist/redux/system/system.selectors";
import { IAction, TActions } from "@rentiohq/web-shared/dist/types";
import utils from "@rentiohq/web-shared/dist/utils";
import { findManagerOrAssignedEmployee } from "@rentiohq/web-shared/dist/utils/members";
import styled from "@rentiohq/web-theme";
import { uniq } from "lodash";
import React, { useCallback, useContext, useMemo } from "react";
import {
  DragDropContext,
  Droppable,
  DroppableProvided,
  DragStart,
  DropResult,
} from "react-beautiful-dnd";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useOutletContext, useLocation } from "react-router-dom";
import { IRootStore } from "redux/reducers";
import * as CONFIG from "scenes/Settings/scenes/TenantProfileNotificationPreferences/TenantProfileNotificationPreferences.config";
import { IFlowItem } from "utils/application.utils";
import createDuplicateApplicationForm from "../../../../forms/duplicateApplication";
import createPropertyInviteApplicationForm from "../../../../forms/propertyInviteApplication";
import * as t from "../../../../services/translationService";
import { ApplicationsFilterPanel } from "./components/ApplicationsFilterPanel/ApplicationsFilterPanel";
import { Table } from "./components/ApplicationTable/ApplicationTable.styled";
import {
  ApplicationTableHeader,
  SortColumn,
  SortDirection,
} from "./components/ApplicationTable/ApplicationTableHeader";
import { ApplicationTableRow } from "./components/ApplicationTable/ApplicationTableRow";
import {
  ApplicationTabs,
  ITab,
} from "./components/ApplicationTabs/ApplicationTabs";
import { ApplicationToolbar } from "./components/ApplicationToolbar/ApplicationToolbar";
import {
  CustomFeedbackModal,
  ICustomFeedbackParams,
} from "./components/CustomFeedbackModal";
import {
  getResidentsCount,
  getTotalIncome,
} from "./components/DragItem/dragItem.utils";
import { FiltersDropdown } from "./components/FiltersDropdown/FiltersDropdown";
import GroupActionsModal from "./components/GroupActionsModal";
import { PropertyAvailabilityConfig } from "./components/PropertyAvailabilityConfig";
import { PropertyDetailApplicationEmptyStateMemo } from "./components/PropertyDetailApplicationEmptyState";
import { SaveFilterModal } from "./components/SaveFilterModal/SaveFilterModal";
import { WarningTooltip } from "./components/WarningTooltip";
import {
  DragAndDropContext,
  dragFinalDropTarget,
} from "./context/DragAndDropContext";
import { IStatusChange } from "./PropertyDetailApplicationsContainer";
import { RM_COLORS } from "./rentmatch.colors";

// Compute the RentMatch score for an application the same way the table row
// does. The score is derived (not a stored field), so both the filter and the
// sort must recompute it from income, household size and the property rent.
// Returns null when the score cannot be computed (no rent known).
const getApplicationRentMatchScore = (
  application: IApplication,
  property: IProperty,
  propertyAmenities: IPropertyAmenity[] | undefined,
): number | null => {
  const rent = (property.price ?? 0) + (property.costs ?? 0);
  if (rent <= 0) {
    return null;
  }

  const propertyDetails = {
    rent,
    bedrooms: propertyAmenities
      ? getAmenityNumberValue(
          propertyAmenities,
          "rentio.default.room_count_bedroom",
        ) || null
      : null,
    availableDate: null,
    hasBalcony: false,
    hasGarden: false,
    floorType: "other" as const,
  };

  const userDetails = {
    netIncome: getTotalIncome(application.contacts),
    otherIncome: 0,
    adults: getResidentsCount(application.residents, "INTAKE_RESIDENT_ADULTS"),
    minors: getResidentsCount(
      application.residents,
      "INTAKE_RESIDENT_CHILDREN",
    ),
    moveInDateType: application.dateOfOccupancy
      ? ("specific" as const)
      : ("flexible" as const),
    specificMoveInDate: application.dateOfOccupancy
      ? String(application.dateOfOccupancy)
      : undefined,
  };

  return calculateRentMatchScore(userDetails, propertyDetails).score;
};

const AvailabilityOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const AvailabilityCard = styled.div`
  background: ${({ theme }) => theme.colors.neutral0};
  border-radius: ${({ theme }) => theme.borderRadius()};
  box-shadow:
    0 0 0 1px rgba(6, 44, 82, 0.1),
    0 2px 16px rgba(33, 43, 54, 0.08);
  width: 1200px;
  max-width: calc(100% - 48px);
  max-height: calc(100% - 48px);
  overflow-y: auto;
`;

const ToolbarLabel = styled.span`
  font-weight: 300;
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  color: ${({ theme }) => theme.getColor("gray", 700)};
  white-space: nowrap;
`;

const ToolbarSlotCount = styled.span<{
  variant?: "success" | "warning" | "muted";
}>`
  font-weight: 300;
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  white-space: nowrap;
  color: ${({ theme, variant }) => {
    if (variant === "success") return theme.colors.green60;
    if (variant === "warning") return theme.colors.yellow[800];
    if (variant === "muted") return theme.getColor("gray", 400);
    return theme.getColor("gray", 500);
  }};
`;

const ToolbarHelpBadge = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.green60};
  font-size: ${({ theme }) => theme.fontSizes[0]}px;
  cursor: help;
  white-space: nowrap;
`;

const ToolbarInlineItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing("tight")};
  flex-shrink: 0;
  white-space: nowrap;

  button {
    background-color: ${({ theme }) => theme.colors.neutral0};
  }
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 24px 8px;
`;

export const SectionIcon = styled.div`
  display: flex;
  align-items: center;
  color: ${RM_COLORS.gray500};
`;

export const SectionTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${RM_COLORS.gray900};
`;

export interface IApplicationDropDownHandler {
  (
    applicationIds: string[],
    step: number,
    duplicatedApplication?: IApplication,
  ): void;
}

interface IProps {
  property: IProperty;
  applications: IApplication[];
  type: number | null;
  visitorSteps?: IFlowItem[];
  candidateSteps: IFlowItem[];
  notEligibleSteps: IFlowItem[];
  handleOneStatusChange: (params: IStatusChange) => void;
  savedFilters?: ISavedApplicationFilter[];
  onSaveFilter?: (
    name: string,
    criteria: IFilterCriteria,
    onSuccess?: (filter: ISavedApplicationFilter) => void,
  ) => void;
  onUpdateFilter?: (
    id: number,
    name: string,
    criteria: IFilterCriteria,
  ) => void;
  onDeleteFilter?: (id: number) => void;
}

export interface IDropArea {
  id: number;
  items: IApplication[];
  isDisabled: boolean;
  allowedSteps: number[];
}

enum ModalVariants {
  Candidate = "candidate",
  Tenant = "tenant",
  NotEligible = "not-eligible",
  Visitor = "visitor",
  PlanForVisit = "plan-for-visit",
  Invited = "invited",
  WaitingList = "waiting-list",
}

const formId = generateFormId();

enum EDropAreaAction {
  Init = "init",
  SetAreas = "setAreas",
  SetPermissions = "setPermissions",
}

interface IDropAreaAction {
  type: EDropAreaAction;
  payload: any;
}

const dropAreaReducer = (state: any[], action: IDropAreaAction) => {
  switch (action.type) {
    case EDropAreaAction.Init:
      return action.payload;

    case EDropAreaAction.SetAreas:
      return state.map((area, index: number) => {
        area.items = action.payload[index].items;
        return area;
      });

    case EDropAreaAction.SetPermissions:
      return state.map((area, index: number) => {
        area.isDisabled = action.payload[index].isDisabled;
        return area;
      });

    default:
  }
};

const PropertyDetailApplications = (props: IProps) => {
  const {
    property,
    applications = [],
    visitorSteps,
    candidateSteps = [],
    notEligibleSteps = [],
    handleOneStatusChange,
    savedFilters = [],
    onSaveFilter,
    onUpdateFilter,
    onDeleteFilter,
  } = props;

  const dispatch = useDispatch();
  const hasTenant = applications.some(a => a.step === EIntakeStep.tenant);

  const { items: propertyAmenities } = propertyAmenityHooks.useGetByPropertyId({
    propertyId: property.id,
  });

  // Derived RentMatch score per application, computed once and reused by the
  // score filter and the RentMatch sort column.
  const rentMatchScoreById = useMemo(() => {
    const scores = new Map<string, number | null>();
    applications.forEach(application => {
      scores.set(
        application.id,
        getApplicationRentMatchScore(application, property, propertyAmenities),
      );
    });
    return scores;
    // Depend on the specific property fields used by the score so unrelated
    // property updates don't recompute every application's score.
  }, [applications, property.price, property.costs, propertyAmenities]);
  const [isLoadingInvite, setLoadingInvite] = React.useState<boolean>(false);
  const [customFeedbackParams, setCustomFeedbackParams] =
    React.useState<ICustomFeedbackParams>();
  const [isGroupActionsModalIsOpen, setGroupActionsModalIsOpen] =
    React.useState(false);
  const [showAvailabilityModal, setShowAvailabilityModal] =
    React.useState<boolean>(false);
  const hasCalendarSync = useBrokerFeature(EBrokerFeature.CalendarSync);

  const [availabilitySlotCount, setAvailabilitySlotCount] = React.useState<
    number | null
  >(null);
  const [, setBookedApplicationIds] = React.useState<Set<string>>(new Set());
  const [applicationVisits, setApplicationVisits] = React.useState<any[]>([]);
  const [isLoadingSlots, setIsLoadingSlots] = React.useState(true);
  const [calendarRemainingSlots, setCalendarRemainingSlots] = React.useState<
    number | null
  >(null);

  const { archive: archiveApplication } = applicationHooks.useArchive({});
  const { unarchive: unarchiveApplication } = applicationHooks.useUnarchive({});
  const [showBulkDuplicateModal, setShowBulkDuplicateModal] =
    React.useState<boolean>(false);
  const [autoWaitlistPromptDetails, setAutoWaitlistPromptDetails] =
    React.useState<{
      statusChanges: any[];
      applications: IApplication[];
    } | null>(null);

  // Filters State
  const [isFilterPanelOpen, setIsFilterPanelOpen] = React.useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = React.useState(false);
  const [activeFilterId, setActiveFilterId] = React.useState<
    number | undefined
  >(undefined);
  const [filterCriteria, setFilterCriteria] = React.useState<IFilterCriteria>(
    {},
  );

  // Load saved availability slots + booked visits to power the split view
  React.useEffect(() => {
    const loadSlots = async () => {
      try {
        setIsLoadingSlots(true);
        const res = await api.get<any>(
          `/cronofy/properties/${property.id}/availability`,
          { isV2: true },
        );
        const blocks =
          res.data?.data?.blocks || res.data?.data || res.data || [];
        const visits = res.data?.data?.visits || [];
        // Parse each active visit's start/end once up front so the per-window
        // booked count below doesn't re-parse dates inside the block loop.
        const activeVisitRanges: { start: number; end: number }[] = visits
          .filter((v: any) => v.status !== "cancelled")
          .map((v: any) => ({
            start: new Date(v.start).getTime(),
            end: new Date(v.end).getTime(),
          }));

        // Count only slots that still lie in the future — expired (past) slots
        // are no longer bookable and must not be shown as "available". We also
        // subtract already-booked visits so the dashboard badge shows the same
        // remaining count as the availability calendar, without having to open
        // the modal first.
        const now = Date.now();
        let totalSlots = 0;
        let bookedSlots = 0;
        if (Array.isArray(blocks)) {
          blocks.forEach((block: any) => {
            const start = new Date(block.start).getTime();
            const end = new Date(block.end).getTime();
            const duration = Number(block.slotDurationMinutes) || 15;
            let capacity =
              block.capacityPerSlot !== undefined
                ? Number(block.capacityPerSlot)
                : 1;

            // Handle 'Unlimited' capacity option
            if (capacity === 0) capacity = 999;

            if (isNaN(start) || isNaN(end) || end <= start || duration <= 0) {
              return;
            }

            const durationMs = duration * 60000;
            for (
              let winStart = start;
              winStart + durationMs <= end;
              winStart += durationMs
            ) {
              const winEnd = winStart + durationMs;
              if (winEnd <= now) continue; // expired window — skip
              totalSlots += capacity;
              bookedSlots += activeVisitRanges.filter(
                v => v.start >= winStart && v.end <= winEnd,
              ).length;
            }
          });
        }
        setAvailabilitySlotCount(totalSlots);
        // Seed the remaining count from load so the badge is correct immediately;
        // the calendar refines it (busy-event overlaps) once the modal opens.
        setCalendarRemainingSlots(Math.max(0, totalSlots - bookedSlots));

        // Extract applicationIds of booked visits for the Visitors / Invited split
        setApplicationVisits(visits);
        const bookedIds = new Set<string>(
          visits
            .filter((v: any) => v.status !== "cancelled" && v.applicationId)
            .map((v: any) => String(v.applicationId)),
        );
        setBookedApplicationIds(bookedIds);
      } catch {
        setAvailabilitySlotCount(0);
        setCalendarRemainingSlots(0);
        setBookedApplicationIds(new Set());
      } finally {
        setIsLoadingSlots(false);
      }
    };
    loadSlots();
  }, [property.id, showAvailabilityModal]);

  const location = useLocation();
  const initialStep = (location.state as any)?.step;

  // Tab navigation
  const [activeTab, setActiveTab] = React.useState<EIntakeStep | "archived">(
    initialStep || EIntakeStep.candidateVisitor,
  );

  React.useEffect(() => {
    const step = (location.state as any)?.step;
    if (step) {
      setActiveTab(step);
    }
  }, [location.state]);

  const [searchQuery, setSearchQuery] = React.useState<string>("");
  const [sortColumn, setSortColumn] = React.useState<SortColumn>(null);
  const [sortDirection, setSortDirection] =
    React.useState<SortDirection>("asc");

  const { user, broker, isBroker } = authHooks.useSelf();

  // Find the administrative manager for this property to default the planner calendar
  const adminManagerUserId = useMemo(() => {
    if (!property?.members || !broker) return undefined;
    const adminManager = findManagerOrAssignedEmployee(
      property.members,
      EPropertyMemberTypes.AdministrativeManager,
      broker,
    );
    return adminManager?.userId;
  }, [property?.members, broker]);

  const { setSecondaryActionNode } = useOutletContext<any>() || {};

  const {
    selectedItems,
    setAllDropAreas,
    replaceSelectedItems,
    setIsGlobalDragging,
  } = useContext(DragAndDropContext);

  const visitorEnabled = useSelector((state: IRootStore) =>
    systemSelectors.getPreference(
      state,
      CONFIG.PREFERENCE_VISITOR_ENABLED,
      true,
    ),
  );

  const notEligibleEnabled = useSelector((state: IRootStore) =>
    systemSelectors.getPreference(
      state,
      CONFIG.PREFERENCE_NOT_ELIGIBLE_ENABLED,
      true,
    ),
  );

  const planForVisitEnabled = useSelector((state: IRootStore) =>
    systemSelectors.getPreference(
      state,
      CONFIG.PREFERENCE_PLAN_FOR_VISIT_ENABLED,
      true,
    ),
  );

  const waitingListEnabled = useSelector((state: IRootStore) =>
    systemSelectors.getPreference(
      state,
      CONFIG.PREFERENCE_WAITING_LIST_ENABLED,
      true,
    ),
  );

  const invitedEnabled = useSelector((state: IRootStore) =>
    systemSelectors.getPreference(
      state,
      CONFIG.PREFERENCE_INVITED_ENABLED,
      true,
    ),
  );

  const [visitorDropAreas, dispatchVisitorDropAreas] = React.useReducer(
    dropAreaReducer,
    [],
  );
  const [candidateDropAreas, dispatchCandidateDropAreas] = React.useReducer(
    dropAreaReducer,
    [],
  );
  const [notEligibleDropAreas, dispatchNotEligibleDropAreas] = React.useReducer(
    dropAreaReducer,
    [],
  );

  const [inviteModalOpen, setInviteModalOpen] = React.useState<boolean>(false);
  const [contactsQueue, setContactsQueue] = React.useState<
    IApplicationContact[]
  >([]);
  const [currentContactIndex, setCurrentContactIndex] =
    React.useState<number>();

  const handleChange = (statusChanges: IStatusChange[]) => {
    statusChanges.forEach(statusChange => {
      handleOneStatusChange(statusChange);
    });
    replaceSelectedItems([]);
  };

  React.useEffect(() => {
    const all = [
      ...visitorDropAreas,
      ...candidateDropAreas,
      ...notEligibleDropAreas,
    ];
    setAllDropAreas(all);
  }, [visitorDropAreas, candidateDropAreas, notEligibleDropAreas]);

  React.useEffect(() => {
    const allAvailableSteps = [
      ...(visitorSteps || []),
      ...candidateSteps,
      ...notEligibleSteps,
    ];
    const availableStepIds: number[] = allAvailableSteps.map(step => step.id);
    const firstAvailableStepId = availableStepIds[0];

    const initDropAreas = (steps: IFlowItem[]) => {
      const dropArea = steps.map((step: any) => {
        return {
          id: step.id,
          items: applications.filter((application: IApplication) => {
            if (application.archivedAt) {
              return false;
            }

            if (application.rejectedAt && step === EIntakeStep.notEligible) {
              return true;
            }

            if (
              !availableStepIds.includes(application.step) &&
              step.id === firstAvailableStepId &&
              isBroker
            ) {
              return true;
            }

            return step.id === application.step;
          }),
          allowedSteps: step.allowedSteps,
          isDisabled:
            step.id === EIntakeStep.invited &&
            (availabilitySlotCount === null || availabilitySlotCount === 0),
        };
      }) as IDropArea[];

      return dropArea;
    };

    dispatchVisitorDropAreas({
      type: EDropAreaAction.Init,
      payload: initDropAreas(visitorSteps || []),
    });

    dispatchCandidateDropAreas({
      type: EDropAreaAction.Init,
      payload: initDropAreas(candidateSteps),
    });

    dispatchNotEligibleDropAreas({
      type: EDropAreaAction.Init,
      payload: initDropAreas(notEligibleSteps),
    });
  }, [
    applications,
    visitorSteps,
    candidateSteps,
    notEligibleSteps,
    availabilitySlotCount,
  ]);

  React.useEffect(() => {
    if (currentContactIndex === undefined) return;

    if (currentContactIndex >= contactsQueue.length) {
      setCurrentContactIndex(undefined);
      setContactsQueue([]);
      return;
    }

    const contact = contactsQueue[currentContactIndex];
    confirm({
      title: getLocalizedText("application.assign.tenant.confirm.heading"),
      info: getLocalizedText("application.assign.tenant.confirm.body", {
        phone: contact.phone || "",
      }),
      secondaryAction: {
        title: getLocalizedText("system.no"),
        onPress: () => {
          setCurrentContactIndex((prevIndex = 0) => prevIndex + 1);
        },
      },
      primaryActions: [
        {
          title: getLocalizedText("system.yes"),
          onPress: () => {
            addTenantAsContact(contact);
            setCurrentContactIndex((prevIndex = 0) => prevIndex + 1);
          },
        },
      ],
    });
  }, [currentContactIndex, contactsQueue]);

  const toggleModal = (params: {
    type: ModalVariants;
    statusChanges: IStatusChange[];
    applications: IApplication[];
  }) => {
    const { type, statusChanges, applications } = params;

    switch (type) {
      case ModalVariants.Candidate:
        confirm({
          title: getLocalizedText(
            "application.assign.candidate.confirm.heading",
          ),
          info: getLocalizedText(
            `application.assign.${
              applications.length > 1 ? "candidates" : "candidate"
            }.confirm.body`,
          ),
          secondaryAction: {
            title: getLocalizedText("system.cancel"),
          },
          primaryActions: [
            {
              title: getLocalizedText("system.ok"),
              onPress: () => handleChange(statusChanges),
            },
          ],
        });

        return;

      case ModalVariants.NotEligible:
      case ModalVariants.PlanForVisit:
      case ModalVariants.Invited:
      case ModalVariants.Visitor:
      case ModalVariants.WaitingList:
        setCustomFeedbackParams({ statusChanges, applications });
        return;

      default:
        return;
    }
  };

  const resetAreaPermissions = () => {
    const newVisitorDropAreaPermissions = visitorDropAreas.map((area: any) => ({
      ...area,
      isDisabled:
        area.id === EIntakeStep.invited &&
        (availabilitySlotCount === null || availabilitySlotCount === 0),
    }));

    const newCandidateDropAreaPermissions = candidateDropAreas.map(
      (area: any) => ({
        ...area,
        isDisabled: false,
      }),
    );

    const newNotEligibleDropAreaPermissions = notEligibleDropAreas.map(
      (area: any) => ({
        ...area,
        isDisabled: false,
      }),
    );

    dispatchVisitorDropAreas({
      type: EDropAreaAction.SetPermissions,
      payload: newVisitorDropAreaPermissions,
    });
    dispatchCandidateDropAreas({
      type: EDropAreaAction.SetPermissions,
      payload: newCandidateDropAreaPermissions,
    });
    dispatchNotEligibleDropAreas({
      type: EDropAreaAction.SetPermissions,
      payload: newNotEligibleDropAreaPermissions,
    });
  };

  const addTenantAsContact = async (contact: any) => {
    const input = utils.object.filterKeys(contact, [
      "firstname",
      "lastname",
      "phone",
      "email",
    ]);
    const address = utils.object.filterKeys(property, [
      "number",
      "street",
      "city",
      "zip",
      "countryId",
      "box",
    ]);
    dispatch(
      contactActions.createContact.actions.start({
        contact: {
          ...input,
          ...address,
          locale: contact.language,
          initialType: EContactInitialType.Tenant,
        },
        sendInvite: true,
        onSuccess: (contact: IContact) => {
          dispatch(
            contactActions.askRemainingData.actions.start({
              contactId: contact.id,
            }),
          );
        },
      }),
    );
  };

  const checkIfTenantIsContact = async (
    statusChanges: IStatusChange[],
    applications: IApplication[],
  ) => {
    const contacts = applications.flatMap(app => app.contacts);
    const results = await Promise.all(
      contacts.map(async contact => {
        const { phone } = contact;
        const { data: existingContacts } = await contactApi.searchContacts("", {
          where: { phone },
          limit: 1,
        });

        return {
          contact,
          exists: existingContacts.length > 0,
          existingContact: existingContacts[0] || null,
        };
      }),
    );

    const newContacts = results.filter(r => !r.exists).map(r => r.contact);
    const existingContacts = results.filter(r => r.exists).map(c => c.contact);

    newContacts.forEach(contact => addTenantAsContact(contact));

    setContactsQueue(existingContacts);
    setCurrentContactIndex(0);
    toggleModal({ type: ModalVariants.Tenant, statusChanges, applications });
  };

  const setAreaItems = (
    sourceAreas: IDropArea[],
    applicationIds: string[],
    destinationAreaKey: string,
  ) => {
    const destinationId = Number(destinationAreaKey);

    const removedApplications = sourceAreas.reduce(
      (allRemovedApplications: IApplication[], sourceArea: IDropArea) => {
        const removedForThisDropArea = sourceArea.items.filter(
          (item: IApplication) => applicationIds.includes(item.id),
        );
        const unique = new Set([
          ...allRemovedApplications,
          ...removedForThisDropArea,
        ]);
        return Array.from(unique);
      },
      [],
    );

    if (!removedApplications) {
      return;
    }

    const statusChanges = removedApplications.map(
      (application: IApplication) => {
        return {
          id: application.id,
          step: destinationId,
        };
      },
    );

    switch (destinationId) {
      case EIntakeStep.waitingList:
        if (waitingListEnabled) {
          toggleModal({
            type: ModalVariants.WaitingList,
            statusChanges,
            applications: removedApplications,
          });
          break;
        }
        handleChange(statusChanges);
        break;

      case EIntakeStep.planAsVisitor:
        if (planForVisitEnabled) {
          toggleModal({
            type: ModalVariants.PlanForVisit,
            statusChanges,
            applications: removedApplications,
          });
          break;
        }
        handleChange(statusChanges);
        break;

      case EIntakeStep.invited:
        // Block if no slots are configured
        if (availabilitySlotCount === null || availabilitySlotCount === 0) {
          toast({
            heading: getLocalizedText("application.invited.no_slots.title"),
            content: getLocalizedText(
              "application.invited.no_slots.description",
            ),
            variation: "error",
          });
          break;
        }
        // Intercept overload if they haven't explicitly set autoWaitlist yet
        if (
          availabilitySlotCount !== null &&
          statusChanges.length > availabilitySlotCount &&
          !property.autoWaitlistForNewApplicants
        ) {
          setAutoWaitlistPromptDetails({
            statusChanges,
            applications: removedApplications,
          });
          break;
        }

        if (invitedEnabled) {
          toggleModal({
            type: ModalVariants.Invited,
            statusChanges,
            applications: removedApplications,
          });
          break;
        }
        handleChange(statusChanges);
        break;
      case EIntakeStep.visitor: {
        if (visitorEnabled) {
          toggleModal({
            type: ModalVariants.Visitor,
            statusChanges,
            applications: removedApplications,
          });
          break;
        }

        handleChange(statusChanges);
        break;
      }

      case EIntakeStep.candidateTenant:
        toggleModal({
          type: ModalVariants.Candidate,
          statusChanges,
          applications: removedApplications,
        });
        break;

      case EIntakeStep.tenant:
        handleChange(statusChanges);
        checkIfTenantIsContact(statusChanges, removedApplications);
        // Move the view to the Tenant tab so the promoted candidate is visible
        // there instead of silently disappearing from the current tab.
        setActiveTab(EIntakeStep.tenant);

        break;
      case EIntakeStep.notEligible: {
        if (notEligibleEnabled) {
          toggleModal({
            type: ModalVariants.NotEligible,
            statusChanges,
            applications: removedApplications,
          });

          break;
        }

        handleChange(statusChanges);
        break;
      }

      default:
        handleChange(statusChanges);
        break;
    }
  };

  const setAreaPermissions = (sourceAreaKey: number) => {
    const allowedVisitorSteps = visitorDropAreas.find(
      (area: any) => area.id === sourceAreaKey,
    )?.allowedSteps;
    const allowedCandidateSteps = candidateDropAreas.find(
      (area: any) => area.id === sourceAreaKey,
    )?.allowedSteps;
    const allowedNotEligibleSteps = notEligibleDropAreas.find(
      (area: any) => area.id === sourceAreaKey,
    )?.allowedSteps;

    const allAllowedSteps = uniq([
      ...(allowedVisitorSteps || []),
      ...(allowedCandidateSteps || []),
      ...(allowedNotEligibleSteps || []),
    ]);

    const newVisitorDropAreaPermissions = visitorDropAreas.map((area: any) => ({
      ...area,
      isDisabled: !allAllowedSteps.includes(area.id),
    }));

    const newCandidatesDropAreaPermissions = candidateDropAreas.map(
      (area: any) => ({
        ...area,
        isDisabled: !allAllowedSteps.includes(area.id),
      }),
    );

    const newNotEligibleDropAreaPermissions = notEligibleDropAreas.map(
      (area: any) => ({
        ...area,
        isDisabled: !allAllowedSteps.includes(area.id),
      }),
    );

    dispatchVisitorDropAreas({
      type: EDropAreaAction.SetPermissions,
      payload: newVisitorDropAreaPermissions,
    });
    dispatchCandidateDropAreas({
      type: EDropAreaAction.SetPermissions,
      payload: newCandidatesDropAreaPermissions,
    });
    dispatchNotEligibleDropAreas({
      type: EDropAreaAction.SetPermissions,
      payload: newNotEligibleDropAreaPermissions,
    });
  };

  // Dropdown handler
  const handler = (
    applicationIds: string[],
    step: number,
    duplicatedApplication?: IApplication,
  ) => {
    if (duplicatedApplication) {
      if (step === EIntakeStep.tenant) {
        checkIfTenantIsContact(
          [{ id: duplicatedApplication.id, step }],
          [duplicatedApplication],
        );
      }

      return;
    }

    const dropAreas = [
      ...visitorDropAreas,
      ...candidateDropAreas,
      ...notEligibleDropAreas,
    ];

    const sourceAreas = applicationIds.map(applicationId =>
      dropAreas.find(dropArea =>
        dropArea.items.find((item: IApplication) => item.id === applicationId),
      ),
    );

    setAreaItems(sourceAreas, applicationIds, step.toString());
  };

  const getSourceAreas = useCallback(
    (applicationIds: string[]) => {
      const allDropAreas = [
        ...visitorDropAreas,
        ...candidateDropAreas,
        ...notEligibleDropAreas,
      ];
      return applicationIds.map(applicationId =>
        allDropAreas.find(dropArea =>
          dropArea.items.find(
            (item: IApplication) => item.id === applicationId,
          ),
        ),
      );
    },
    [visitorDropAreas, candidateDropAreas, notEligibleDropAreas],
  );

  const onDragStart = (result: DragStart): void => {
    setIsGlobalDragging(true);
    const { source, draggableId: applicationId } = result;

    const sourceId = source.droppableId;
    setAreaPermissions(+sourceId);

    //See if the application being dragged it is included in the multiselect
    const draggedApplicationIsInSelection = !!selectedItems.find(
      i => i.id === applicationId,
    );

    if (!draggedApplicationIsInSelection) {
      // Replace the selected items with the dragged item
      replaceSelectedItems(applications.filter(i => i.id === applicationId));
    }
  };

  const onDragEnd = (result: DropResult): void => {
    setIsGlobalDragging(false);
    const { source, draggableId: applicationId } = result;

    resetAreaPermissions();

    // Read the mouseup snapshot — decided at the exact millisecond of release.
    const destinationId = dragFinalDropTarget || "";

    if (!destinationId) {
      return;
    }

    let sourceId = source.droppableId;
    if (sourceId.startsWith("tab-")) {
      sourceId = sourceId.replace("tab-", "");
    }

    // Skip if dropped back on the same tab/area
    if (sourceId === destinationId) {
      return;
    }

    // Validate that the destination is an allowed step for the source
    // (skip for "archived" — archiving is always allowed)
    if (destinationId !== "archived") {
      const sourceArea =
        visitorDropAreas.find((a: any) => a.id === +sourceId) ||
        candidateDropAreas.find((a: any) => a.id === +sourceId) ||
        notEligibleDropAreas.find((a: any) => a.id === +sourceId);
      if (sourceArea && !sourceArea.allowedSteps?.includes(+destinationId)) {
        return;
      }
    }

    //See if the application being dragged it is included in the multiselect
    const draggedApplicationIsInSelection = !!selectedItems.find(
      i => i.id === applicationId,
    );

    let sourceAreas = [];
    let applicationIds = [];
    if (draggedApplicationIsInSelection) {
      // If it is -> move all selected items
      sourceAreas = getSourceAreas(selectedItems.map(i => i.id));
      applicationIds = selectedItems.map(i => i.id);
    } else {
      // If it is not -> move only the dragged item
      // select this one
      sourceAreas = getSourceAreas([applicationId]);
      applicationIds = [applicationId];
    }

    // Handle drop on the archive tab: archive instead of changing step
    if (destinationId === "archived") {
      applicationIds.forEach(id => archiveApplication({}, id));
      replaceSelectedItems([]);
      return;
    }

    setAreaItems(sourceAreas, applicationIds, destinationId);
  };

  const handleInvite = async (input: any) => {
    try {
      setLoadingInvite(true);
      const { data } = await propertyApi.sendApplicationInvitation(
        property.id,
        input,
      );
      if (data.data.success) {
        toast({
          heading: t.propertyInviteApplicationToastSucceededHeading(),
          variation: "success",
        });
      } else {
        throw new Error();
      }

      handleInviteModalClose();
      setLoadingInvite(false);
    } catch (unknownError) {
      const error = unknownError as any;
      logger.logError({ error });
      toast({
        heading: t.propertyInviteApplicationToastFailedHeading(),
        content: t.propertyInviteApplicationToastFailedContent(),
        variation: "error",
      });
    }
  };

  const handleInviteModalClose = () => {
    setInviteModalOpen(false);
  };

  const handleInviteModalOpen = () => {
    setInviteModalOpen(true);
  };

  const renderGroupActionButton = () => {
    if (selectedItems.length === 0) {
      return null;
    }

    const selectedIds = selectedItems.map(item => item.id);

    // Build context-aware step actions based on active tab
    const positiveActions: { content: string; step: EIntakeStep }[] = [];
    const negativeActions: { content: string; step: EIntakeStep }[] = [];

    switch (activeTab) {
      case EIntakeStep.candidateVisitor:
        if (hasCalendarSync) {
          // "Plan for visit" is only offered when the two-step flow is enabled
          // in the office settings (it defaults to on).
          if (planForVisitEnabled) {
            positiveActions.push({
              content: getLocalizedText("application.action.plan_visit"),
              step: EIntakeStep.planAsVisitor,
            });
          }
          positiveActions.push({
            content: getLocalizedText("application.action.invite"),
            step: EIntakeStep.invited,
          });
        } else {
          positiveActions.push({
            content: getLocalizedText("application.action.visit"),
            step: EIntakeStep.visitor,
          });
        }
        negativeActions.push({
          content: getLocalizedText("application.action.waitlist"),
          step: EIntakeStep.waitingList,
        });
        negativeActions.push({
          content: getLocalizedText("application.action.reject"),
          step: EIntakeStep.notEligible,
        });
        break;
      case EIntakeStep.waitingList:
        if (hasCalendarSync) {
          if (planForVisitEnabled) {
            positiveActions.push({
              content: getLocalizedText("application.action.plan_visit"),
              step: EIntakeStep.planAsVisitor,
            });
          }
          positiveActions.push({
            content: getLocalizedText("application.action.invite"),
            step: EIntakeStep.invited,
          });
        } else {
          positiveActions.push({
            content: getLocalizedText("application.action.visit"),
            step: EIntakeStep.visitor,
          });
        }
        negativeActions.push({
          content: getLocalizedText("application.action.reject"),
          step: EIntakeStep.notEligible,
        });
        break;
      case EIntakeStep.planAsVisitor:
        if (hasCalendarSync) {
          positiveActions.push({
            content: getLocalizedText("application.action.invite"),
            step: EIntakeStep.invited,
          });
        } else {
          positiveActions.push({
            content: getLocalizedText("application.action.visit"),
            step: EIntakeStep.visitor,
          });
        }
        negativeActions.push({
          content: getLocalizedText("application.action.reject"),
          step: EIntakeStep.notEligible,
        });
        break;
      case EIntakeStep.invited:
        positiveActions.push({
          content: getLocalizedText("application.action.visit"),
          step: EIntakeStep.visitor,
        });
        negativeActions.push({
          content: getLocalizedText("application.action.reject"),
          step: EIntakeStep.notEligible,
        });
        break;
      case EIntakeStep.visitor:
        positiveActions.push({
          content: getLocalizedText("application.action.candidate_full"),
          step: EIntakeStep.candidateTenant,
        });
        negativeActions.push({
          content: getLocalizedText("application.action.reject"),
          step: EIntakeStep.notEligible,
        });
        break;
      case EIntakeStep.candidateTenant:
        positiveActions.push({
          content: getLocalizedText("application.action.tenant"),
          step: EIntakeStep.tenant,
        });
        negativeActions.push({
          content: getLocalizedText("application.action.reject"),
          step: EIntakeStep.notEligible,
        });
        break;
    }

    // For archived tab: Activate again + Send message
    if (activeTab === "archived") {
      const bulkActions: TActions = [
        [
          {
            content: getLocalizedText("applications.cta.unarchive"),
            onClick: () => {
              selectedIds.forEach(id => unarchiveApplication({}, id));
              replaceSelectedItems([]);
            },
          },
        ],
        [
          {
            content: getLocalizedText("application.action.send_message"),
            onClick: () => setGroupActionsModalIsOpen(true),
          },
        ],
      ];

      return (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <DropdownMenu
            actions={bulkActions}
            children={
              <Button
                appearance="outline"
                color="gray"
                iconAfterName="chevronDown"
              >
                {`${getLocalizedText("system.bulk_action")} (${
                  selectedItems.length
                })`}
              </Button>
            }
          />
        </div>
      );
    }

    const bulkActions: TActions = [
      // Group 1: Positive step changes
      ...(positiveActions.length > 0
        ? [
            positiveActions.map(action => ({
              content: action.content,
              onClick: () => {
                handler(selectedIds, action.step);
                replaceSelectedItems([]);
              },
            })),
          ]
        : []),
      // Group 2: Negative step changes
      ...(negativeActions.length > 0
        ? [
            negativeActions.map(action => ({
              content: action.content,
              onClick: () => {
                handler(selectedIds, action.step);
                replaceSelectedItems([]);
              },
            })),
          ]
        : []),
      // Group 3: Utility actions
      [
        {
          content: getLocalizedText("application.action.send_message"),
          onClick: () => setGroupActionsModalIsOpen(true),
        },
      ],
      // Group 4: Archive
      [
        {
          content: getLocalizedText("applications.cta.archive"),
          onClick: () => {
            confirm({
              type: "warning",
              title: getLocalizedText(
                "application.action.archive_confirm.title",
                {
                  count: String(selectedItems.length),
                },
              ),
              primaryActions: [
                {
                  title: getLocalizedText("applications.cta.archive"),
                  onPress: async () => {
                    try {
                      await Promise.all(
                        selectedIds.map(id =>
                          api.post(`/applications/${id}/archive`),
                        ),
                      );
                      toast({
                        heading: getLocalizedText(
                          "application.action.archive_confirm.success",
                          {
                            count: String(selectedItems.length),
                          },
                        ),
                        variation: "success",
                      });
                      replaceSelectedItems([]);
                    } catch (e) {
                      toast({
                        heading: getLocalizedText(
                          "application.action.archive_confirm.error",
                        ),
                        variation: "error",
                      });
                    }
                  },
                },
              ],
            });
          },
        },
      ],
    ];

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <DropdownMenu
          actions={bulkActions}
          children={
            <Button appearance="primary" iconAfterName="chevronDown">
              {`${getLocalizedText("system.bulk_action")} (${
                selectedItems.length
              })`}
            </Button>
          }
        />
      </div>
    );
  };

  const handleGroupActionsModalClose = () => {
    setGroupActionsModalIsOpen(false);
    replaceSelectedItems([]);
  };

  const renderGroupActionsModal = () => {
    if (!isGroupActionsModalIsOpen) {
      return;
    }

    return (
      <GroupActionsModal
        selectedApplicants={selectedItems}
        property={property}
        onCancel={handleGroupActionsModalClose}
        onConfirm={() => {
          replaceSelectedItems([]);
          setGroupActionsModalIsOpen(false);
        }}
      ></GroupActionsModal>
    );
  };

  // Compute tabs data from drop areas
  const allDropAreas = useMemo(
    () => [...visitorDropAreas, ...candidateDropAreas, ...notEligibleDropAreas],
    [visitorDropAreas, candidateDropAreas, notEligibleDropAreas],
  );

  const archivedApplications = useMemo(
    () => applications.filter(a => !!a.archivedAt),
    [applications],
  );

  const tabs: ITab[] = useMemo(() => {
    // Phase = source group, no exceptions:
    // visitorDropAreas → phase 1 (New, Waiting list, To be scheduled, Invited, Visitors)
    // candidateDropAreas → phase 2 (Candidate tenants, Tenants)
    // notEligibleDropAreas → undefined/gray (Not eligible)
    const makeTab = (area: IDropArea, phase: 1 | 2 | undefined) => ({
      id: area.id as EIntakeStep,
      label: t.applicationStateTitleRentmatch(area.id),
      count: area.items.length,
      isDisabled: area.isDisabled,
      phase,
    });

    const tabList: ITab[] = [
      ...visitorDropAreas.map((area: IDropArea) => makeTab(area, 1)),
      ...candidateDropAreas.map((area: IDropArea) => makeTab(area, 2)),
      ...notEligibleDropAreas.map((area: IDropArea) =>
        makeTab(area, undefined),
      ),
    ];

    tabList.push({
      id: "archived",
      // Dashboard-owned key: applications.tab.archive is shared with the tenant
      // profile flow, so this uses a separate key to avoid changing it there.
      label: getLocalizedText("applications.tab.archived"),
      count: archivedApplications.length,
    });

    return tabList;
  }, [allDropAreas, archivedApplications]);

  // Set initial active tab to first available (prefer non-archived)
  React.useEffect(() => {
    const regularTabs = tabs.filter(tab => tab.id !== "archived");
    // Only reset if there are real (non-archived) tabs loaded AND current tab is not among them
    if (regularTabs.length === 0) return; // data still loading — keep initial state
    const isCurrentTabValid = regularTabs.find(tab => tab.id === activeTab);
    if (!isCurrentTabValid) {
      setActiveTab(regularTabs[0].id);
    }
  }, [tabs]);

  // Get current tab's items (filtered + sorted)
  const currentTabItems: IApplication[] = useMemo(() => {
    if (activeTab === "archived") {
      let items = archivedApplications;

      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        items = items.filter((item: IApplication) =>
          item.contacts.some(contact =>
            getName(contact).toLowerCase().includes(query),
          ),
        );
      }

      // Sort
      if (sortColumn) {
        items = [...items].sort((a: IApplication, b: IApplication) => {
          let comparison = 0;
          switch (sortColumn) {
            case "income":
              comparison =
                getTotalIncome(a.contacts) - getTotalIncome(b.contacts);
              break;
            case "activity": {
              const dateA = a.archivedAt || "";
              const dateB = b.archivedAt || "";
              comparison =
                new Date(dateA).getTime() - new Date(dateB).getTime();
              break;
            }
            case "candidate": {
              const nameA = getName(a.contacts?.[0] || {}) || "";
              const nameB = getName(b.contacts?.[0] || {}) || "";
              comparison = nameA.localeCompare(nameB);
              break;
            }
            default:
              break;
          }
          return sortDirection === "desc" ? -comparison : comparison;
        });
      }

      return items;
    }

    const area = allDropAreas.find(
      (area: IDropArea) => area.id === (activeTab as number),
    );
    let items = area?.items || [];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      items = items.filter((item: IApplication) =>
        item.contacts.some(contact =>
          getName(contact).toLowerCase().includes(query),
        ),
      );
    }

    // Advanced Filters
    if (filterCriteria) {
      if (filterCriteria.incomeMultiplier) {
        const minIncome =
          (property.price || 0) * filterCriteria.incomeMultiplier;
        items = items.filter(
          (item: IApplication) => getTotalIncome(item.contacts) >= minIncome,
        );
      }
      if (filterCriteria.maxAdults) {
        items = items.filter((item: IApplication) => {
          const adults =
            getResidentsCount(item.residents, "INTAKE_RESIDENT_ADULTS") ||
            item.contacts.length;
          return adults <= filterCriteria.maxAdults!;
        });
      }
      // `maxChildren` may legitimately be 0 (candidates without children), so
      // check against null instead of a truthy check that would skip 0.
      if (filterCriteria.maxChildren != null) {
        items = items.filter((item: IApplication) => {
          const children = getResidentsCount(
            item.residents,
            "INTAKE_RESIDENT_CHILDREN",
          );
          return children <= filterCriteria.maxChildren!;
        });
      }
      if (filterCriteria.minApplications) {
        // The options read "> N applications" (candidates applying to more than
        // one property), so filter strictly greater than the chosen threshold.
        items = items.filter((item: IApplication) => {
          const totalApplications =
            1 + (item.otherApplicationsMeta?.properties?.length || 0);
          return totalApplications > filterCriteria.minApplications!;
        });
      }
      if (filterCriteria.minRating) {
        items = items.filter(
          (item: IApplication) =>
            (item.rating || 0) >= filterCriteria.minRating!,
        );
      }
      if (filterCriteria.rentMatchMinScore) {
        items = items.filter((item: IApplication) => {
          const score = rentMatchScoreById.get(item.id);
          return score != null && score >= filterCriteria.rentMatchMinScore!;
        });
      }
    }

    // Sort
    if (sortColumn) {
      items = [...items].sort((a: IApplication, b: IApplication) => {
        let comparison = 0;
        switch (sortColumn) {
          case "income":
            comparison =
              getTotalIncome(a.contacts) - getTotalIncome(b.contacts);
            break;
          case "activity": {
            const dateA = a.stepCompletedAt || a.stepStartedAt || "";
            const dateB = b.stepCompletedAt || b.stepStartedAt || "";
            comparison = new Date(dateA).getTime() - new Date(dateB).getTime();
            break;
          }
          case "candidate": {
            const nameA = getName(a.contacts?.[0] || {}) || "";
            const nameB = getName(b.contacts?.[0] || {}) || "";
            comparison = nameA.localeCompare(nameB);
            break;
          }
          case "rentmatch":
            comparison =
              (rentMatchScoreById.get(a.id) || 0) -
              (rentMatchScoreById.get(b.id) || 0);
            break;
          case "rating":
            comparison = (a.rating || 0) - (b.rating || 0);
            break;
          default:
            break;
        }
        return sortDirection === "desc" ? -comparison : comparison;
      });
    }

    return items;
  }, [
    allDropAreas,
    archivedApplications,
    activeTab,
    searchQuery,
    sortColumn,
    sortDirection,
    filterCriteria,
    property.price,
    rentMatchScoreById,
  ]);

  // Get the current active drop area for the table Droppable
  const activeDropArea = useMemo(
    () =>
      allDropAreas.find((area: IDropArea) => area.id === (activeTab as number)),
    [activeTab, allDropAreas],
  );

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Invite actions for the toolbar
  const inviteActions: IAction[] = useMemo(() => {
    const actions: IAction[] = [];
    actions.push({
      content: getLocalizedText(
        "property.application.invite.email_action.label",
      ),
      onClick: handleInviteModalOpen,
      size: "small",
      appearance: "primary",
    });

    if (getTenantProfileUrl(property, broker)) {
      actions.push({
        content: getLocalizedText(
          "property.application.invite.url_action.label",
        ),
        onClick: async () => {
          const url = getTenantProfileUrl(property, broker);
          if (url) {
            try {
              await navigator.clipboard.writeText(url);
              toast({
                heading: getLocalizedText("system.copied_to_clipboard"),
                variation: "success",
              });
            } catch (e) {
              logger.logError({ error: e as any });
            }
          }
        },
        size: "small",
        appearance: "primary",
      });
    }

    return actions;
  }, [property, broker]);

  React.useEffect(() => {
    if (setSecondaryActionNode) {
      setSecondaryActionNode(
        <DropdownMenu
          children={
            <Button appearance="primary" iconAfterName="chevronDown">
              {getLocalizedText(
                "property.application.invite.main_action.label",
              )}
            </Button>
          }
          actions={[inviteActions]}
        />,
      );
    }
    return () => {
      if (setSecondaryActionNode) {
        setSecondaryActionNode(undefined);
      }
    };
  }, [setSecondaryActionNode, inviteActions]);

  if (!visitorSteps && !candidateSteps && !notEligibleSteps) {
    return <PropertyDetailApplicationEmptyStateMemo />;
  }

  const autoWaitlistNode = (
    <ToolbarInlineItem>
      <ToolbarLabel>
        {getLocalizedText("property.applications.auto_waitlist.toolbar_label")}
      </ToolbarLabel>
      <Tooltip
        tooltipContent={getLocalizedText(
          "property.applications.auto_waitlist.what_is_this_tooltip",
        )}
        placement="bottom"
      >
        <ToolbarHelpBadge>(?)</ToolbarHelpBadge>
      </Tooltip>
      <Switch
        isChecked={property.autoWaitlistForNewApplicants || false}
        onChange={val => {
          dispatch(
            propertyActions.updateStart.getAction({
              id: property.id,
              data: { autoWaitlistForNewApplicants: val },
              onSuccess: () => {
                toast({
                  heading: val
                    ? getLocalizedText(
                        "property.applications.auto_waitlist.toast_enabled",
                      )
                    : getLocalizedText(
                        "property.applications.auto_waitlist.toast_disabled",
                      ),
                  variation: "success",
                });
              },
              onFailure: e => {
                logger.logError({ error: e as any });
              },
            }),
          );
        }}
      />
    </ToolbarInlineItem>
  );

  const calendarNode = hasCalendarSync
    ? (() => {
        const total = availabilitySlotCount ?? 0;
        const remaining = calendarRemainingSlots ?? total;
        const slotVariant = total > 0 ? "success" : "warning";
        return (
          <ToolbarInlineItem>
            <ToolbarLabel>
              {getLocalizedText(
                "property.applications.availability.toolbar_label",
              )}
            </ToolbarLabel>
            {isLoadingSlots ? (
              <ToolbarSlotCount variant="muted">…</ToolbarSlotCount>
            ) : (
              <ToolbarSlotCount variant={slotVariant}>
                ({remaining}/{total})
              </ToolbarSlotCount>
            )}
            {!isLoadingSlots && total === 0 && (
              <WarningTooltip
                tooltipContent={getLocalizedText(
                  "property.applications.availability.no_slots_warning",
                )}
              />
            )}
            <Button
              appearance="outline"
              color="gray"
              size="extraSmall"
              onClick={() => setShowAvailabilityModal(true)}
            >
              {total > 0
                ? getLocalizedText("system.adjust")
                : getLocalizedText("property.applications.availability.setup")}
            </Button>
          </ToolbarInlineItem>
        );
      })()
    : null;

  return (
    <>
      {showAvailabilityModal &&
        hasCalendarSync &&
        createPortal(
          <AvailabilityOverlay
            onClick={e => {
              if (e.target === e.currentTarget) setShowAvailabilityModal(false);
            }}
          >
            <AvailabilityCard>
              <PropertyAvailabilityConfig
                propertyId={property.id}
                propertyAddress={(
                  (property.street || "") +
                  " " +
                  (property.number || "")
                ).trim()}
                onClose={() => setShowAvailabilityModal(false)}
                defaultBrokerUserId={adminManagerUserId}
                availabilitySlotCount={availabilitySlotCount}
                isLoadingSlots={isLoadingSlots}
                onRemainingCountChange={setCalendarRemainingSlots}
                hasTenant={hasTenant}
                noWrapper
              />
            </AvailabilityCard>
          </AvailabilityOverlay>,
          document.body,
        )}

      {autoWaitlistPromptDetails && (
        <Modal
          width="medium"
          onClose={() => setAutoWaitlistPromptDetails(null)}
          heading={getLocalizedText(
            "property.applications.auto_waitlist.modal.title",
          )}
          actions={[
            {
              content: getLocalizedText(
                "property.applications.auto_waitlist.modal.button.cancel",
              ),
              onClick: () => {
                if (planForVisitEnabled) {
                  toggleModal({
                    type: ModalVariants.PlanForVisit,
                    statusChanges: autoWaitlistPromptDetails.statusChanges,
                    applications: autoWaitlistPromptDetails.applications,
                  });
                } else {
                  handleChange(autoWaitlistPromptDetails.statusChanges);
                }
                setAutoWaitlistPromptDetails(null);
              },
              appearance: "outline",
            },
            {
              content: getLocalizedText(
                "property.applications.auto_waitlist.modal.button.confirm",
              ),
              onClick: () => {
                dispatch(
                  propertyActions.updateStart.getAction({
                    id: property.id,
                    data: { autoWaitlistForNewApplicants: true },
                    onSuccess: () => {
                      toast({
                        heading: getLocalizedText(
                          "property.applications.auto_waitlist.toast_enabled",
                        ),
                        variation: "success",
                      });
                    },
                    onFailure: e => {
                      logger.logError({ error: e as any });
                    },
                  }),
                );

                if (planForVisitEnabled) {
                  toggleModal({
                    type: ModalVariants.PlanForVisit,
                    statusChanges: autoWaitlistPromptDetails.statusChanges,
                    applications: autoWaitlistPromptDetails.applications,
                  });
                } else {
                  handleChange(autoWaitlistPromptDetails.statusChanges);
                }
                setAutoWaitlistPromptDetails(null);
              },
              appearance: "primary",
            },
          ]}
        >
          <div style={{ padding: "24px 16px" }}>
            <p style={{ margin: 0, fontSize: "14px", lineHeight: "1.5" }}>
              {getLocalizedText(
                "property.applications.auto_waitlist.modal.description",
              )}
            </p>
          </div>
        </Modal>
      )}

      <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <Card>
          <ApplicationTabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={tabId => {
              replaceSelectedItems([]);
              setActiveTab(tabId);
            }}
          />

          <ApplicationToolbar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            groupActionNode={renderGroupActionButton()}
            filterNode={
              <FiltersDropdown
                savedFilters={savedFilters}
                activeFilterId={activeFilterId}
                onSelectFilter={f => {
                  setActiveFilterId(f?.id);
                  setFilterCriteria(f?.criteria || {});
                }}
                onShowAllFilters={() => {
                  setActiveFilterId(undefined);
                }}
                onDeleteFilter={id => onDeleteFilter?.(id)}
                isPanelOpen={isFilterPanelOpen}
                onTogglePanel={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
                activeFilterCount={Object.keys(filterCriteria).length}
              />
            }
            autoWaitlistNode={
              activeTab === EIntakeStep.candidateVisitor ||
              activeTab === EIntakeStep.waitingList
                ? autoWaitlistNode
                : undefined
            }
            calendarNode={calendarNode}
          />

          {isFilterPanelOpen && (
            <ApplicationsFilterPanel
              activeFilterName={
                savedFilters?.find(f => f.id === activeFilterId)?.name
              }
              filterCriteria={filterCriteria}
              onChange={setFilterCriteria}
              onSave={() => {
                if (activeFilterId && onUpdateFilter) {
                  const activeFilter = savedFilters?.find(
                    f => f.id === activeFilterId,
                  );
                  if (activeFilter) {
                    onUpdateFilter(
                      activeFilter.id,
                      activeFilter.name,
                      filterCriteria,
                    );
                    setIsFilterPanelOpen(false);
                    return;
                  }
                }
                setIsSaveModalOpen(true);
              }}
              onClear={() => {
                setFilterCriteria({});
                setActiveFilterId(undefined);
              }}
              onClose={() => setIsFilterPanelOpen(false)}
              isEditing={!!activeFilterId}
            />
          )}

          <SaveFilterModal
            isOpen={isSaveModalOpen}
            onClose={() => setIsSaveModalOpen(false)}
            onSave={name => {
              onSaveFilter?.(name, filterCriteria, newFilter => {
                setActiveFilterId(newFilter.id);
              });
              setIsSaveModalOpen(false);
              setIsFilterPanelOpen(false);
            }}
          />

          {(() => {
            const isArchived = activeTab === "archived";

            return (
              <Table>
                <ApplicationTableHeader
                  items={currentTabItems}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />

                <Droppable
                  droppableId={`${activeTab}`}
                  direction="vertical"
                  isDropDisabled={false}
                  isCombineEnabled={false}
                  ignoreContainerClipping={false}
                >
                  {(provided: DroppableProvided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {currentTabItems.length === 0 ? (
                        <Box my={ESpacings.base}>
                          <EmptyState
                            backgroundColor="gray"
                            heading={
                              isArchived
                                ? getLocalizedText(
                                    "applications.section.archived",
                                  )
                                : t.applicationStatePlaceholderRentmatch(
                                    activeTab as number,
                                  )
                            }
                          />
                        </Box>
                      ) : (
                        currentTabItems.map(
                          (item: IApplication, index: number) => (
                            <ApplicationTableRow
                              key={item.id}
                              item={item}
                              step={isArchived ? 0 : (activeTab as number)}
                              index={index}
                              applicationVisit={applicationVisits.find(
                                v =>
                                  String(v.applicationId) === String(item.id) &&
                                  v.status !== "cancelled",
                              )}
                              allowedSteps={
                                isArchived
                                  ? []
                                  : activeDropArea?.allowedSteps || []
                              }
                              isDisabled={activeDropArea?.isDisabled}
                              renderAsDragItem={!isArchived}
                              isSelected={selectedItems
                                .map(si => si.id)
                                .includes(item.id)}
                              handler={handler}
                            />
                          ),
                        )
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </Table>
            );
          })()}
        </Card>
      </DragDropContext>

      {inviteModalOpen && (
        <MultiStepForm
          formId={`invite-user-application-${formId}`}
          schemas={createPropertyInviteApplicationForm()}
          asModal={true}
          withAside={false}
          onSuccess={handleInvite}
          submitLabel={t.system("submit")}
          modalProps={{ onClose: handleInviteModalClose, width: "small" }}
          isLoading={isLoadingInvite}
        />
      )}

      {customFeedbackParams && user && (
        <CustomFeedbackModal
          customFeedbackParams={customFeedbackParams}
          user={user}
          broker={broker}
          property={property}
          formId={formId}
          isLoadingInvite={isLoadingInvite}
          handleChange={handleChange}
          setCustomFeedbackParams={setCustomFeedbackParams}
        />
      )}

      {renderGroupActionsModal()}

      {showBulkDuplicateModal && selectedItems.length === 1 && (
        <MultiStepForm
          formId={`bulk-duplicate-application-${formId}`}
          schemas={createDuplicateApplicationForm({
            broker,
            propertyId: property.id,
          })}
          asModal={true}
          withAside={false}
          onSuccess={({ step, propertyId: newPropertyId }: any) => {
            if (!newPropertyId) return;
            dispatch(
              applicationActions.duplicateApplicationStart.getAction({
                id: selectedItems[0].id,
                propertyId: newPropertyId,
                step,
                onSuccess(result: any) {
                  handler?.([result.id], step, result);
                  toast({
                    heading: getLocalizedText("system.duplicate") + " ✓",
                    variation: "success",
                  });
                  replaceSelectedItems([]);
                },
              }),
            );
            setShowBulkDuplicateModal(false);
          }}
          modalProps={{
            onClose: () => setShowBulkDuplicateModal(false),
            width: "small",
          }}
        />
      )}
    </>
  );
};

const PropertyDetailApplicationsMemo = React.memo(
  PropertyDetailApplications,
  utils.components.arePropsEqual,
);

// eslint-disable-next-line import/no-default-export
export default PropertyDetailApplicationsMemo;
